import type { Maybe, OptionalPromise, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import '@adornis/forms/x-input.js';
import '@adornis/popover/x-dropdown-selection.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import '@ils/locations/client/ils-location-selection.js';
import { Location } from '@ils/locations/db/location.js';
import { ExamDayRatingType } from '@ils/static/enums.js';
import type { brandDict } from '@ils/translation/translation.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { until } from 'lit/directives/until.js';
import { ILSUser } from '../../../db/ils-user.js';
import { examDayListFilter } from '../../store/filterStore.js';
import '../ils-simple-collapsable.js';
import './../ils-user-selection.js';
import './exd-timebar-button.js';

interface FilterCollapsable {
  filterAttribute: string;
  label: string;
  onValuePicked?: (e: ValueEvent<any>, filterAttribute: string) => void;
  selectables?: string[];
  renderString?: (item: Maybe<any>) => string;
}

interface FilterCollapsableCustom {
  filterAttribute: string;
  label: string;
  labelValue?: (value: any) => OptionalPromise<string>;
  render: () => Renderable;
}

@customElement('exd-filter-pills')
export class ExdFilterPills extends ChemistryLitElement {
  @state() private collapsed = true;

  @state() private readonly _filter = new RXController(this, examDayListFilter);
  @state() private readonly _translation = new TranslationController<brandDict>(this);

  @state() filters: Array<FilterCollapsable | FilterCollapsableCustom> = [
    {
      filterAttribute: 'isPublished',
      label: 'Veröffentlicht',
      onValuePicked: (e: ValueEvent<boolean | undefined>, filterAttribute: string) =>
        this.valueEventToFilter(e, filterAttribute),
    },
    {
      filterAttribute: 'isPublishedForTesters',
      label: 'Für Schauspieler Veröffentlicht',
      onValuePicked: (e: ValueEvent<boolean | undefined>, filterAttribute: string) =>
        this.valueEventToFilter(e, filterAttribute),
    },
    {
      filterAttribute: 'isPlanned',
      label: 'Geplant',
      onValuePicked: (e: ValueEvent<boolean | undefined>, filterAttribute: string) =>
        this.valueEventToFilter(e, filterAttribute),
    },
    {
      filterAttribute: 'hasCandidates',
      label: 'Teilnehmer zugewiesen',
      onValuePicked: (e: ValueEvent<boolean | undefined>, filterAttribute: string) => {
        this.valueEventToFilter(e, filterAttribute);
      },
    },
    {
      filterAttribute: 'hasFallbackTesters',
      label: 'Ersatz-Schauspieler zugewiesen',
      onValuePicked: (e: ValueEvent<boolean | undefined>, filterAttribute: string) => {
        this.valueEventToFilter(e, filterAttribute);
      },
    },
    {
      filterAttribute: 'ratingType',
      label: 'Prüfungstag-Typ',
      onValuePicked: (e: ValueEvent<ExamDayRatingType | undefined>, filterAttribute: string) => {
        this.valueEventToFilter(e, filterAttribute);
      },
      selectables: Object.values(ExamDayRatingType),
      renderString: (examDayType: Maybe<ExamDayRatingType>) =>
        examDayType ? (this._translation.translate('ratingType', examDayType, 'short') as string) : '',
    },
    {
      filterAttribute: 'examCenterID',
      label: 'Prüfungskoordinator',
      labelValue: (value: string) =>
        getByID(
          ILSUser,
          value,
        )({ ecommerce: { firstName: 1, lastName: 1, fullName: 1 } }).then(user => user?.ecommerce?.fullName ?? ''),
      render: () => html`
        <ils-user-selection
          ${css({ minWidth: '260px' })}
          placeholder="Bitte auswählen"
          clearable
          @value-picked=${(e: ValueEvent<Maybe<string>>) => this.valueEventToFilter(e, 'examCenterID')}
          .value=${this._filter.value.examCenterID}
        ></ils-user-selection>
      `,
    },
    {
      filterAttribute: 'locationID',
      label: 'Prüfungsstandort',
      labelValue: (value: string) => getByID(Location, value)({ displayName: 1 }).then(loc => loc?.displayName ?? ''),
      render: () => html`
        <ils-location-selection
          ${css({ minWidth: '260px' })}
          placeholder="Bitte auswählen"
          clearable
          @value-picked=${(e: ValueEvent<Maybe<string>>) => this.valueEventToFilter(e, 'locationID')}
          .value=${this._filter.value.locationID}
        ></ils-location-selection>
      `,
    },
  ];

  valueEventToFilter(e: ValueEvent<any>, filterAttribute: string) {
    this._filter.value[filterAttribute] = e.detail.value;

    this._filter.next(this._filter.value);
  }

  override render() {
    return html`
      <ils-simple-collapsable
        .collapsed=${this.collapsed}
        @toggle=${e => {
          this.collapsed = e.detail.value;
        }}
      >
        <div slot="static">
          <x-flex horizontal space="sm" crossaxis-center>
            <exd-timebar-button toggle width="auto" icon="${this.collapsed ? 'filter_alt' : 'expand_less'}">
              Filter
            </exd-timebar-button>
            ${repeat(
              this.filters
                .filter(
                  filter =>
                    this._filter.value[filter.filterAttribute] !== undefined &&
                    this._filter.value[filter.filterAttribute] !== null,
                )
                .map(filter => ({ ...filter, value: this._filter.value[filter.filterAttribute] })),
              filter => {
                return html`
                  <exd-timebar-button
                    width="auto"
                    icon="close"
                    tone="subtle"
                    @click=${() => {
                      this._filter.value[filter.filterAttribute] = undefined;
                      this._filter.next(this._filter.value);
                    }}
                  >
                    <x-flex horizontal crossaxis-center>
                      <x-text ellipsis .value=${filter.label}> </x-text>
                      <x-text>
                        :
                        ${'labelValue' in filter
                          ? until(filter.labelValue?.(filter.value), '...')
                          : typeof filter.value === 'boolean'
                          ? filter.value
                            ? 'Ja'
                            : 'Nein'
                          : 'renderString' in filter
                          ? filter.renderString?.(filter.value)
                          : filter.value}
                      </x-text>
                    </x-flex>
                  </exd-timebar-button>
                `;
              },
            )}
          </x-flex>
        </div>
        <x-flex space="sm" slot="content"> ${this.filterForm()} </x-flex>
      </ils-simple-collapsable>
    `;
  }

  filterForm() {
    return html`
      ${repeat(
        this.filters,
        filter => filter.filterAttribute,
        filter => {
          return html`
            <x-flex horizontal crossaxis-center>
              <x-text ${css({ minWidth: '260px' })}> ${filter.label} </x-text>
              ${'render' in filter
                ? filter.render()
                : html`
                    <x-dropdown-selection
                      ${css({ minWidth: '260px' })}
                      .selectables=${filter.selectables ?? [undefined, true, false]}
                      .renderString=${val => {
                        if (filter.renderString) return filter.renderString(val);
                        if (val === undefined || val === null) {
                          return 'Keine Angabe';
                        } else if (val === true) {
                          return 'Ja';
                        } else {
                          return 'Nein';
                        }
                      }}
                      .value=${this._filter.value[filter.filterAttribute]}
                      placeholder="Bitte auswählen"
                      @value-picked=${(e: any) => {
                        filter.onValuePicked?.(e, filter.filterAttribute);
                      }}
                    >
                    </x-dropdown-selection>
                  `}
            </x-flex>
          `;
        },
      )}
    `;
  }
}
